import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Shield, Moon, GraduationCap, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/storyforge/Navbar";
import { SF } from "../constants/testIds";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

// Enchanted-forest hero — golden light through indigo-blue canopy, storybook feel.
// Replace this Unsplash placeholder with a hand-illustrated hero when brand asset arrives.
const HERO_MAGIC = "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?crop=entropy&cs=srgb&fm=jpg&w=1600&h=1200&fit=crop&q=85";
const ART_WATERCOLOR = "https://images.unsplash.com/photo-1627229045047-b53784b1c121?crop=entropy&cs=srgb&fm=jpg&w=1000&h=1000&fit=crop&q=85";
const ART_STORYBOOK = "https://images.unsplash.com/photo-1669580141374-24ec0a4184b2?crop=entropy&cs=srgb&fm=jpg&w=1000&h=1000&fit=crop&q=85";

const THEMES = ["Courage", "Kindness", "Curiosity", "Confidence", "Friendship", "Bravery", "Empathy", "Imagination"];

export default function Landing() {
    useDocumentMeta({
        title: "Athena's Atlas — Where Your Child Becomes The Hero",
        description: "A premium 24-page keepsake children's book starring your child, custom-illustrated to look just like them. Built for parents who want every bedtime story to be unforgettable.",
    });

    return (
        <div className="min-h-screen paper-bg">
            <Navbar />

            {/* =============================== HERO ============================== */}
            <section
                data-testid={SF.landingHero}
                className="relative overflow-hidden"
            >
                {/* Magical forest background with golden glow layered on top of paper-bg */}
                <div className="absolute inset-0 pointer-events-none">
                    <img
                        src={HERO_MAGIC}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-[0.55]"
                    />
                    {/* Deep indigo → warm golden gradient scrim so the text stays readable */}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#1D1B4B]/85 via-[#2A2560]/70 to-[#3B3378]/60" />
                    {/* Golden glow spotlights */}
                    <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,_rgba(255,206,120,0.35)_0%,_transparent_65%)]" />
                    <div className="absolute -bottom-40 right-10 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,_rgba(255,184,89,0.28)_0%,_transparent_70%)]" />
                </div>

                <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-24 md:pb-40 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <p className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.35em] text-[#FFD98A] mb-8">
                            <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Athena&apos;s Atlas <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                        </p>
                        <h1
                            data-testid="sf-hero-h1"
                            className="font-serif text-5xl sm:text-6xl md:text-7xl text-[#FFF7E4] tracking-tight leading-[1.02] [text-shadow:_0_4px_24px_rgb(0_0_0_/_45%)]"
                        >
                            Welcome to Athena&apos;s Atlas
                        </h1>
                        <p
                            data-testid="sf-hero-dedication"
                            className="mt-6 md:mt-8 font-serif italic text-lg md:text-2xl text-[#FFE3A3] leading-relaxed max-w-2xl mx-auto [text-shadow:_0_2px_12px_rgb(0_0_0_/_50%)]"
                        >
                            Athena, my daughter the warrior. Atlas, my son, my world.
                        </p>
                        <p
                            data-testid="sf-hero-subhead"
                            className="mt-10 text-base md:text-lg text-[#EADFC8]/90 max-w-2xl mx-auto leading-relaxed"
                        >
                            Your child is already a hero. Athena&apos;s Atlas makes them the hero of their own premium, custom-illustrated adventure — because every bedtime story should be unforgettable.
                        </p>
                        <div className="mt-12 flex flex-wrap gap-4 justify-center">
                            <Link to="/signup" data-testid={SF.landingCta}>
                                <Button
                                    className="group relative rounded-full h-14 px-10 text-base font-semibold text-[#1D1B4B] bg-gradient-to-b from-[#FFD98A] to-[#F5B841] hover:from-[#FFE3A3] hover:to-[#FFC963] shadow-[0_10px_40px_-8px_rgba(245,184,65,0.6)] hover:shadow-[0_16px_50px_-8px_rgba(245,184,65,0.8)] hover:-translate-y-0.5 transition-all duration-300 border border-[#FFF7E4]/20"
                                >
                                    <Sparkles className="w-5 h-5 mr-2 -ml-1" strokeWidth={2} />
                                    Start the Magic
                                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                                </Button>
                            </Link>
                            <a
                                href="#origin"
                                className="inline-flex items-center h-14 px-6 text-[#FFE3A3] hover:text-[#FFF7E4] underline underline-offset-4 decoration-[#FFD98A]/60 hover:decoration-[#FFF7E4]"
                            >
                                Read our story
                            </a>
                        </div>
                        <p className="mt-8 text-sm text-[#EADFC8]/70">A 24-page hardcover keepsake · Free US shipping · Arrives in 7–10 days</p>
                    </motion.div>
                </div>

                {/* Bottom fade into paper background */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#F8F1E2] pointer-events-none" />
            </section>

            {/* ============================ THEME MARQUEE ========================= */}
            <section className="border-y border-border bg-muted/40 py-5 overflow-hidden">
                <div className="marquee-track flex gap-12 font-serif italic text-muted-foreground text-xl">
                    {[...THEMES, ...THEMES, ...THEMES].map((t, i) => (
                        <span key={`${t}-${i}`} className="whitespace-nowrap">
                            {t} <span className="text-accent">·</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* =================== MISSION & ORIGIN STORY (TRUCK DRIVER) ================== */}
            <section
                id="origin"
                data-testid="sf-origin-story"
                className="relative overflow-hidden"
            >
                <div className="absolute inset-0 bg-gradient-to-b from-[#F8F1E2] via-[#F5EAD0] to-[#F8F1E2] pointer-events-none" />
                <div className="relative max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32">
                    <div className="flex items-center gap-4 mb-10 justify-center">
                        <span className="h-px flex-1 bg-[#D4A24C]/40" />
                        <span className="text-xs uppercase tracking-[0.35em] text-[#8A6A2E]">A Note From The Creator</span>
                        <span className="h-px flex-1 bg-[#D4A24C]/40" />
                    </div>
                    <h2
                        data-testid="sf-origin-title"
                        className="font-serif italic text-4xl md:text-5xl text-[#2A2560] tracking-tight text-center mb-12 leading-[1.15]"
                    >
                        Built for the parents who want to make every minute count.
                    </h2>
                    <div className="font-serif text-lg md:text-xl text-foreground leading-[1.85] space-y-6">
                        <p>
                            <span className="text-[#8A6A2E] font-semibold">I&apos;m a truck driver.</span> When my 3 children were young, I spent countless nights out on the road, miles away from my babies.
                        </p>
                        <p>
                            While some parents got to experience the magic of the bedtime story ritual every single night, I was lucky if I got to do it once a month. Because my time at home was so limited, I wanted those rare bedtime stories to mean something real.
                        </p>
                        <p>
                            But I couldn&apos;t find a children&apos;s book that captured that for my kids, nor for myself. <span className="italic">That is why I created Athena&apos;s Atlas.</span>
                        </p>
                        <p>
                            We use AI to custom-illustrate your child into a premium keepsake book where they are the hero of their own world.
                        </p>
                        <p className="pt-4 text-center font-serif italic text-xl md:text-2xl text-[#2A2560]">
                            Whether you get to read to your child every night, or only a few times a year like I did — every story should be a memory that lasts forever.
                        </p>
                    </div>
                    <div className="flex items-center gap-4 mt-14 justify-center">
                        <span className="h-px flex-1 bg-[#D4A24C]/40" />
                        <span className="w-2 h-2 rounded-full bg-[#D4A24C]" />
                        <span className="h-px flex-1 bg-[#D4A24C]/40" />
                    </div>
                </div>
            </section>

            {/* ===================== POWER OF THE STORY ============================ */}
            <section
                data-testid="sf-power-of-story"
                className="relative overflow-hidden bg-gradient-to-b from-[#1D1B4B] via-[#2A2560] to-[#1D1B4B] text-[#FFF7E4]"
            >
                {/* Golden atmospheric spotlights */}
                <div className="absolute top-20 -left-20 w-[400px] h-[400px] rounded-full bg-[radial-gradient(circle,_rgba(255,206,120,0.20)_0%,_transparent_65%)]" />
                <div className="absolute bottom-20 -right-20 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,_rgba(255,184,89,0.18)_0%,_transparent_70%)]" />

                <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32">
                    <div className="text-center mb-14">
                        <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-[#FFD98A] mb-6">
                            <Sparkles className="w-4 h-4" strokeWidth={1.5} /> The Power of the Story <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                        </p>
                        <h2
                            data-testid="sf-power-title"
                            className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.05]"
                        >
                            Empower them to conquer their world.
                        </h2>
                        <p className="mt-8 text-base md:text-lg text-[#EADFC8]/90 max-w-2xl mx-auto leading-relaxed">
                            More than just a magical adventure, Athena&apos;s Atlas is a tool for your child&apos;s growth. Because you control the narrative, you can tailor the story&apos;s core lesson to whatever your child is facing right now.
                        </p>
                        <p className="mt-4 text-base md:text-lg text-[#EADFC8]/90 max-w-2xl mx-auto leading-relaxed">
                            Whether they need a boost of self-confidence, help conquering a fear of the dark, or encouragement to be kind — <span className="text-[#FFD98A] font-semibold">you decide the lesson</span>. By seeing their own face bravely overcoming challenges on the page, they learn to conquer them in real life.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-16">
                        {[
                            {
                                icon: Shield,
                                label: "Build Confidence",
                                body: "Watch them become the brave, capable hero of their own story.",
                                testid: "sf-power-confidence",
                            },
                            {
                                icon: Moon,
                                label: "Conquer Fears",
                                body: "From darkness to doctors' visits — turn worries into adventures they master.",
                                testid: "sf-power-fears",
                            },
                            {
                                icon: GraduationCap,
                                label: "Learn Life Lessons",
                                body: "Kindness, honesty, patience — any lesson, woven into a story just for them.",
                                testid: "sf-power-lessons",
                            },
                        ].map(({ icon: Icon, label, body, testid }, i) => (
                            <motion.div
                                key={label}
                                data-testid={testid}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                className="rounded-2xl bg-[#FFF7E4]/[0.04] border border-[#FFD98A]/25 backdrop-blur-sm p-8 text-center hover:border-[#FFD98A]/50 hover:bg-[#FFF7E4]/[0.06] transition-colors"
                            >
                                <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center bg-gradient-to-b from-[#FFD98A] to-[#F5B841] shadow-[0_8px_24px_-6px_rgba(245,184,65,0.6)]">
                                    <Icon className="w-6 h-6 text-[#1D1B4B]" strokeWidth={2} />
                                </div>
                                <h3 className="font-serif text-2xl text-[#FFF7E4] mb-3">{label}</h3>
                                <p className="text-sm text-[#EADFC8]/85 leading-relaxed">{body}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ========================= HOW IT WORKS ============================ */}
            <section id="how" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
                <div className="max-w-2xl mb-16 text-center mx-auto">
                    <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">How it works</p>
                    <h2 className="font-serif text-4xl md:text-5xl text-primary tracking-tight">Four steps from idea to bedtime.</h2>
                </div>
                <div className="grid md:grid-cols-4 gap-6">
                    {[
                        { title: "Tell us about them", body: "Name, a theme, a lesson to teach, and the look you love." },
                        { title: "We write 24 pages", body: "Claude crafts a custom picture book starring your child." },
                        { title: "We illustrate it", body: "A character-consistent illustration on every page." },
                        { title: "We print and ship", body: "Heavy paper, square hardcover, mailed to your door." },
                    ].map(({ title, body }, i) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            className="paper-card rounded-2xl p-6 relative"
                        >
                            <div className="w-9 h-9 rounded-full bg-gradient-to-b from-[#FFD98A] to-[#F5B841] text-[#1D1B4B] font-serif font-bold flex items-center justify-center mb-5 text-lg">
                                {i + 1}
                            </div>
                            <h3 className="font-serif text-xl text-primary mb-2">{title}</h3>
                            <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ======================== SAMPLE ART =============================== */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32 grid md:grid-cols-12 gap-8 items-end">
                <div className="md:col-span-5 md:pb-12">
                    <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">Illustration styles</p>
                    <h2 className="font-serif text-4xl md:text-5xl text-primary tracking-tight">From watercolor whimsy to pencil dreams.</h2>
                    <p className="mt-6 text-muted-foreground leading-relaxed">
                        Pick a style and we&apos;ll make sure every page feels like one cohesive book — same character, same world, page after page.
                    </p>
                </div>
                <div className="md:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
                    <div className="paper-card rounded-3xl overflow-hidden aspect-square md:translate-y-12">
                        <img src={ART_WATERCOLOR} alt="watercolor illustration" className="w-full h-full object-cover" />
                    </div>
                    <div className="paper-card rounded-3xl overflow-hidden aspect-square">
                        <img src={ART_STORYBOOK} alt="storybook illustration" className="w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            {/* =========================== PRICING =============================== */}
            <section className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
                <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">One price, everything included</p>
                <h2 className="font-serif text-5xl md:text-6xl text-primary tracking-tight">
                    $45<span className="text-3xl text-muted-foreground"> / book</span>
                </h2>
                <p className="mt-6 text-muted-foreground max-w-xl mx-auto leading-relaxed">
                    24 illustrated pages · hardcover · 8.5 × 8.5 inches · free US shipping. Sequels are 20% off — we remember your character.
                </p>
                <Link to="/signup" data-testid={SF.landingPricingCta}>
                    <Button className="mt-10 rounded-full h-14 px-10 text-base font-semibold text-[#1D1B4B] bg-gradient-to-b from-[#FFD98A] to-[#F5B841] hover:from-[#FFE3A3] hover:to-[#FFC963] shadow-[0_10px_40px_-8px_rgba(245,184,65,0.6)] hover:-translate-y-0.5 transition-all">
                        <Sparkles className="w-5 h-5 mr-2" strokeWidth={2} />
                        Start the Magic
                    </Button>
                </Link>
            </section>

            <footer className="border-t border-border py-10 text-center text-sm text-muted-foreground">
                <p>Athena&apos;s Atlas · made with care for the bedtime ritual.</p>
            </footer>
        </div>
    );
}
